import { Navigate, createBrowserRouter } from 'react-router-dom';
import { AppShell } from '@/layouts/AppShell';
import { ProtectedRoute } from './ProtectedRoute';
import { LoginPage } from '@/features/auth/LoginPage';
import { AgentsListPage } from '@/features/agents/AgentsListPage';
import { AgentDetailPage } from '@/features/agents/AgentDetailPage';
import { SkillsListPage } from '@/features/skills/SkillsListPage';
import { SkillDetailPage } from '@/features/skills/SkillDetailPage';
import { DashboardPlaceholder } from '@/features/dashboard/DashboardPlaceholder';
import { NotFoundPage } from '@/features/errors/NotFoundPage';
import { ToolsListPage } from '@/features/tools/ToolsListPage';
import { ActionPoliciesPage } from '@/features/action-policies/ActionPoliciesPage';
import { AllMerchantsPage } from '@/features/merchants/AllMerchantsPage';
import { KnowledgeListPage } from '@/features/knowledge/KnowledgeListPage';
import { KnowledgeDetailPage } from '@/features/knowledge/KnowledgeDetailPage';
import { PhraseListLibraryPage } from '@/features/phrase-lists/PhraseListLibraryPage';
import { PrebuiltPromptsPage } from '@/features/prompts/PrebuiltPromptsPage';
import { SystemPromptsPage } from '@/features/prompts/SystemPromptsPage';
import { ValidationsListPage } from '@/features/validations/ValidationsListPage';
import { RecentRunsPage } from '@/features/validations/RecentRunsPage';
import { RunDetailPage } from '@/features/validations/RunDetailPage';
import { ComparePage } from '@/features/validations/compare/ComparePage';
import { ScenarioLibraryPage } from '@/features/validations/ScenarioLibraryPage';
import { ReviewersPage } from '@/features/reviewers/ReviewersPage';
import { ModelsPage } from '@/features/models/ModelsPage';
import { FoundryDeploymentsPage } from '@/features/foundry/FoundryDeploymentsPage';
import { ModelPricingPage } from '@/features/models/ModelPricingPage';
import { CostsPage } from '@/features/costs/CostsPage';
import { InsightsPage } from '@/features/insights/InsightsPage';
import { ActivityPage } from '@/features/activity/ActivityPage';
import { OperationsPage } from '@/features/operations/OperationsPage';
import { SystemHealthPage } from '@/features/system/SystemHealthPage';
import { LookupsPage } from '@/features/lookups/LookupsPage';
import { AuditLogPage } from '@/features/audit/AuditLogPage';
import { WorkflowsListPage } from '@/features/workflows/WorkflowsListPage';
import { WorkflowDetailPage } from '@/features/workflows/WorkflowDetailPage';
import { InstructionLintPage } from '@/features/lint/InstructionLintPage';
import { RuleCatalogPage } from '@/features/lint/RuleCatalogPage';
import { ElementCatalogPage } from '@/features/briefing-context/ElementCatalogPage';
import { BriefingsListPage } from '@/features/briefing-context/BriefingsListPage';
import { BriefingDetailPage } from '@/features/briefing-context/BriefingDetailPage';
import { AgentBriefingPage } from '@/features/briefing-context/AgentBriefingPage';
import { StudioModulesPage } from '@/features/studio-modules/StudioModulesPage';
import { EvaluationWorkspace } from '@/features/scenario-intelligence/workspace/EvaluationWorkspace';
import { OverviewTab } from '@/features/scenario-intelligence/workspace/OverviewTab';
import { CoverageTab, ReviewsTab, ScenariosTab } from '@/features/scenario-intelligence/workspace/WorkspaceTabs';

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <DashboardPlaceholder /> },

          // Workspace (merchant-scoped)
          { path: 'agents', element: <AgentsListPage /> },
          { path: 'agents/:id', element: <AgentDetailPage /> },
          { path: 'agents/:id/briefings', element: <AgentBriefingPage /> },
          { path: 'skills', element: <SkillsListPage /> },
          { path: 'skills/:id', element: <SkillDetailPage /> },
          { path: 'knowledge', element: <KnowledgeListPage /> },
          { path: 'knowledge/:id', element: <KnowledgeDetailPage /> },
          { path: 'phrase-lists', element: <PhraseListLibraryPage /> },
          { path: 'prompts', element: <PrebuiltPromptsPage /> },
          { path: 'system-prompts', element: <SystemPromptsPage /> },
          { path: 'instruction-lint', element: <InstructionLintPage /> },
          { path: 'workflows', element: <WorkflowsListPage /> },
          { path: 'workflows/:id', element: <WorkflowDetailPage /> },
          { path: 'briefings', element: <BriefingsListPage /> },
          { path: 'briefings/:id', element: <BriefingDetailPage /> },
          { path: 'briefing-elements', element: <ElementCatalogPage /> },
          { path: 'validations', element: <ValidationsListPage /> },
          { path: 'runs', element: <RecentRunsPage /> },
          // SI-07 Compare: a child of Recent runs until the Scenario Intelligence workspace route (/evaluation/compare/:id) exists.
          { path: 'runs/compare', element: <ComparePage /> },
          { path: 'runs/compare/:id', element: <ComparePage /> },
          { path: 'runs/:id', element: <RunDetailPage /> },
          { path: 'scenarios', element: <ScenarioLibraryPage /> },
          // Scenario Intelligence (manifest 3): one destination, six tabs as nested routes. Scenarios, Runs, Compare and
          // Reviews are the existing pages and the review queue; /scenarios, /runs and /runs/compare keep working beside them.
          {
            path: 'evaluation',
            element: <EvaluationWorkspace />,
            children: [
              { index: true, element: <OverviewTab /> },
              { path: 'coverage', element: <CoverageTab /> },
              { path: 'scenarios', element: <ScenariosTab /> },
              { path: 'scenarios/:id', element: <ScenariosTab /> },
              { path: 'runs', element: <RecentRunsPage /> },
              { path: 'runs/:id', element: <RunDetailPage /> },
              { path: 'compare', element: <ComparePage /> },
              { path: 'compare/:id', element: <ComparePage /> },
              { path: 'reviews', element: <ReviewsTab /> },
            ],
          },
          // Approvals was a coming-soon page, retired by manifest 3; its bookmarks land on the review queue.
          { path: 'approvals', element: <Navigate to="/evaluation/reviews" replace /> },
          { path: 'reviewers', element: <ReviewersPage /> },

          // Platform (cross-merchant)
          { path: 'models', element: <ModelsPage /> },
          { path: 'foundry/deployments', element: <FoundryDeploymentsPage /> },
          { path: 'pricing', element: <ModelPricingPage /> },
          { path: 'costs', element: <CostsPage /> },
          { path: 'insights', element: <InsightsPage /> },
          { path: 'activity', element: <ActivityPage /> },
          { path: 'operations', element: <OperationsPage /> },
          { path: 'system', element: <SystemHealthPage /> },
          { path: 'plugins', element: <ToolsListPage /> },
          { path: 'platform/action-policies', element: <ActionPoliciesPage /> },
          { path: 'lookups', element: <LookupsPage /> },
          { path: 'platform/rules', element: <RuleCatalogPage /> },
          { path: 'platform/merchants', element: <AllMerchantsPage /> },
          { path: 'platform/audit', element: <AuditLogPage /> },
          // Administration → Modules: the module catalog (admin-only on the server; the page says so to anyone else).
          { path: 'platform/modules', element: <StudioModulesPage /> },

          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
